import { Box, IconButton, Stack, Typography } from '@mui/joy';
import { Plus, TrashSimple } from '@phosphor-icons/react';
import { t } from 'i18next';
import { Control, useFieldArray } from 'react-hook-form';

import { partFieldService } from 'categories/services';
import { ICategoryPartField } from 'categories/types';
import { Button, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { PartFieldOperator } from 'corporate/enums';
import { getOperatorOptions, IPricingProfilePartFieldCriteriaForm } from 'pricing/components';
import { Color, Level, Size, Variant } from 'theme/enums';

interface IProps {
  name: string;
}

export const PartSpecificationField = ({ name }: IProps) => {
  const { control, watch, errors } = useFormContext();

  const { fields, append, remove } = useFieldArray<{
    [key: string]: IPricingProfilePartFieldCriteriaForm[];
  }>({
    control: control as unknown as Control<{
      [key: string]: IPricingProfilePartFieldCriteriaForm[];
    }>,
    name,
  });

  const addCriteria = () => {
    append({
      partField: null,
      operator: PartFieldOperator.EQ,
      value: '',
      uom: null,
    } as IPricingProfilePartFieldCriteriaForm);
  };

  const removeCriteria = (index: number) => {
    remove(index);
  };

  return (
    <Stack gap={2}>
      <Typography level={Level.TITLE_MD}>{t('labelPartSpecificationCriteria')}</Typography>

      {fields.length > 0 && (
        <Stack gap={0}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '30% 20% 20% 20% 10%',
              gap: 1,
            }}
          >
            <Typography level={Level.BODY_SM}>{t('labelPartField')}</Typography>
            <Typography level={Level.BODY_SM}>{t('labelOperation')}</Typography>
            <Typography level={Level.BODY_SM}>{t('labelValue')}</Typography>
            <Typography level={Level.BODY_SM}>{t('labelUom')}</Typography>
            <Box />
          </Box>

          {fields.map((field, index) => {
            const selectedPartField = watch(
              `${name}.${index}.partField`
            ) as ICategoryPartField | null;

            const fieldErrors = errors?.[name]?.[index];

            return (
              <Box
                key={field.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '30% 20% 20% 20% 10%',
                  gap: 1,
                }}
              >
                <FormField
                  name={`${name}.${index}.partField`}
                  type={InputType.AUTOCOMPLETE}
                  inputProps={{
                    getOptions: partFieldService.getPartFields,
                    required: true,
                    control,
                    error: fieldErrors?.partField,
                  }}
                />
                <FormField
                  name={`${name}.${index}.operator`}
                  type={InputType.SELECT}
                  inputProps={{
                    options: getOperatorOptions(),
                    required: true,
                    control,
                    error: fieldErrors?.operator,
                  }}
                />
                <FormField
                  name={`${name}.${index}.value`}
                  type={InputType.TEXT_INPUT}
                  inputProps={{
                    required: true,
                    control,
                    error: fieldErrors?.value,
                  }}
                />
                <FormField
                  key={`${name}.${index}.uom-${selectedPartField?.id || 'no-part'}`}
                  name={`${name}.${index}.uom`}
                  type={InputType.AUTOCOMPLETE}
                  inputProps={{
                    defaultOptions: selectedPartField?.uoms || [],
                    disabled: !selectedPartField?.uoms?.length,
                    control,
                    error: fieldErrors?.uom,
                  }}
                />
                <Box>
                  <IconButton
                    color={Color.DANGER}
                    size={Size.SM}
                    onClick={() => removeCriteria(index)}
                  >
                    <TrashSimple />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}

      <Button
        text={t('buttonLabelAddPartSpecificationCriteria')}
        variant={Variant.PLAIN}
        onClick={addCriteria}
        startDecorator={<Plus size={16} />}
        sx={{ width: 'fit-content' }}
      />
    </Stack>
  );
};
